import type { ButtonProps } from '@mui/material';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import Form from '@rjsf/mui';
import {
  getTemplate,
  getUiOptions,
  type ArrayFieldItemTemplateProps,
  type IconButtonProps,
  type RJSFSchema,
} from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import {
  COORDINATE_SYSTEMS,
  type BoundKind,
  type ThresholdBundleFormController,
  type ThresholdDefinitionCreate,
} from './threshold-bundle-form.hook';
import { ThresholdPeriodRangePicker } from './ThresholdPeriodRangePicker';

// -- type definitions --

interface ThresholdParametersFieldProps {
  schema: RJSFSchema | null;
  uiSchema: Record<string, unknown>;
  formData: Record<string, unknown>;
  isLoading: boolean;
  errorMessage: string | null;
  onChange: (value: Record<string, unknown>) => void;
}

export interface ThresholdDefinitionStepProps {
  controller: ThresholdBundleFormController;
}

// -- constants --

const COORDINATE_SYSTEM_LABELS: Record<
  (typeof COORDINATE_SYSTEMS)[number],
  string
> = {
  time_series: 'Série Temporelle',
  cartesian_2d: 'Cartésien 2D',
  polar_2d: 'Polaire 2D',
  cartesian_3d: 'Cartésien 3D',
  polar_3d: 'Polaire 3D',
};

const REPRESENTATION_MODE_LABELS: Record<string, string> = {
  sampled: 'Échantillonné',
  mesh: 'Maillé',
};

// -- functions --

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const toMuiActionButtonProps = (
  props: IconButtonProps
): Pick<ButtonProps, 'id' | 'className' | 'disabled' | 'onClick' | 'title'> => {
  return {
    id: props.id,
    className: props.className,
    disabled: props.disabled,
    onClick: props.onClick as ButtonProps['onClick'],
    title: props.title,
  };
};

const ThresholdAddButton = (props: IconButtonProps) => {
  const buttonProps = toMuiActionButtonProps(props);

  return (
    <Button {...buttonProps} type="button" size="small" variant="outlined">
      Ajouter
    </Button>
  );
};

const ThresholdRemoveButton = (props: IconButtonProps) => {
  const buttonProps = toMuiActionButtonProps(props);

  return (
    <Button
      {...buttonProps}
      type="button"
      size="small"
      variant="outlined"
      color="error"
    >
      Supprimer
    </Button>
  );
};

const ThresholdArrayFieldItemTemplate = (
  props: ArrayFieldItemTemplateProps
) => {
  const { children, buttonsProps, hasToolbar, uiSchema, registry } = props;
  const uiOptions = getUiOptions(uiSchema);
  const ArrayFieldItemButtonsTemplate = getTemplate(
    'ArrayFieldItemButtonsTemplate',
    registry,
    uiOptions
  );

  return (
    <Stack spacing={1.25} sx={{ mb: 1.5 }}>
      {children}
      {hasToolbar && (
        <Stack direction="row" justifyContent="flex-end">
          <ArrayFieldItemButtonsTemplate {...buttonsProps} />
        </Stack>
      )}
    </Stack>
  );
};

// -- helper components --

function ThresholdParametersField({
  schema,
  uiSchema,
  formData,
  isLoading,
  errorMessage,
  onChange,
}: ThresholdParametersFieldProps) {
  if (isLoading) {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Chargement du schéma de paramètres...
        </Typography>
      </Stack>
    );
  }

  if (errorMessage) {
    return <Alert severity="warning">{errorMessage}</Alert>;
  }

  if (!schema) {
    return (
      <Alert severity="info">
        Sélectionnez un type de seuil pour configurer ses paramètres.
      </Alert>
    );
  }

  return (
    <Form
      schema={schema}
      uiSchema={uiSchema}
      templates={{
        ArrayFieldItemTemplate: ThresholdArrayFieldItemTemplate,
        ButtonTemplates: {
          AddButton: ThresholdAddButton,
          RemoveButton: ThresholdRemoveButton,
        },
      }}
      formData={formData}
      validator={validator}
      noHtml5Validate
      showErrorList={false}
      tagName="div"
      children={true}
      onChange={(event) => {
        const nextFormData = isPlainObject(event.formData)
          ? event.formData
          : {};
        onChange(nextFormData);
      }}
    />
  );
}

// -- main component --

export function ThresholdDefinitionStep({
  controller,
}: ThresholdDefinitionStepProps) {
  const {
    formValues,
    thresholdTypeOptions,
    thresholdTypesError,
    isThresholdTypesLoading,
    allowedCoordinateSystems,
    allowedRepresentationModes,
  } = controller;
  const errors = controller.validationErrors;

  const showCoordinateSelectors = allowedCoordinateSystems.length > 1;
  const showRepresentationModeSelector = allowedRepresentationModes.length > 1;
  const selectedThresholdTypeOption =
    thresholdTypeOptions.find(
      (option) => option.id_name === formValues.definition.threshold_type
    ) ?? null;

  return (
    <Stack spacing={3}>
      {thresholdTypesError && (
        <Alert severity="warning">{thresholdTypesError}</Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
        <TextField
          required
          value={formValues.definition.name}
          onChange={(event) =>
            controller.setDefinitionField('name', event.target.value)
          }
          error={Boolean(errors.definition_name)}
          helperText={errors.definition_name ?? 'Nom métier du seuil'}
          fullWidth
          label="Nom"
        />

        <Autocomplete
          options={thresholdTypeOptions}
          value={selectedThresholdTypeOption}
          loading={isThresholdTypesLoading}
          onChange={(_, value) =>
            controller.setDefinitionField(
              'threshold_type',
              value?.id_name ?? ''
            )
          }
          isOptionEqualToValue={(option, value) =>
            option.id_name === value.id_name
          }
          getOptionLabel={(option) =>
            option.pretty_name?.trim() || option.id_name
          }
          disableClearable={false}
          renderInput={(params) => (
            <TextField
              {...params}
              required
              label="Type de seuil"
              error={Boolean(errors.definition_threshold_type)}
              helperText={
                errors.definition_threshold_type ??
                'Choisissez un type natif ou une extension disponible'
              }
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isThresholdTypesLoading ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </Stack>

      <TextField
        value={formValues.definition.description}
        onChange={(event) =>
          controller.setDefinitionField('description', event.target.value)
        }
        fullWidth
        multiline
        minRows={2}
        label="Description"
        helperText="Optionnel"
      />

      <Stack spacing={1}>
        <Typography variant="body2" color="text.secondary">
          Type de borne
        </Typography>
        <ToggleButtonGroup
          value={formValues.definition.bound_kind}
          exclusive
          onChange={(_, value) => {
            if (value) {
              controller.setDefinitionField('bound_kind', value as BoundKind);
            }
          }}
        >
          <ToggleButton value="maximum">Maximum</ToggleButton>
          <ToggleButton value="minimum">Minimum</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {(showCoordinateSelectors || showRepresentationModeSelector) && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          {showCoordinateSelectors && (
            <>
              <FormControl fullWidth>
                <InputLabel>Système de coordonnées d'évaluation</InputLabel>
                <Select
                  value={formValues.definition.evaluation_coordinate_system}
                  label="Système de coordonnées d'évaluation"
                  onChange={(event) =>
                    controller.setDefinitionField(
                      'evaluation_coordinate_system',
                      event.target
                        .value as ThresholdDefinitionCreate['evaluation_coordinate_system']
                    )
                  }
                >
                  {allowedCoordinateSystems.map((cs) => (
                    <MenuItem key={cs} value={cs}>
                      {COORDINATE_SYSTEM_LABELS[cs]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>
                  Système de coordonnées de représentation
                </InputLabel>
                <Select
                  value={formValues.definition.representation_coordinate_system}
                  label="Système de coordonnées de représentation"
                  onChange={(event) =>
                    controller.setDefinitionField(
                      'representation_coordinate_system',
                      event.target
                        .value as ThresholdDefinitionCreate['representation_coordinate_system']
                    )
                  }
                >
                  {allowedCoordinateSystems.map((cs) => (
                    <MenuItem key={cs} value={cs}>
                      {COORDINATE_SYSTEM_LABELS[cs]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {showRepresentationModeSelector && (
            <FormControl fullWidth>
              <InputLabel>Mode de représentation</InputLabel>
              <Select
                value={formValues.definition.representation_mode}
                label="Mode de représentation"
                onChange={(event) =>
                  controller.setDefinitionField(
                    'representation_mode',
                    event.target
                      .value as ThresholdDefinitionCreate['representation_mode']
                  )
                }
              >
                {allowedRepresentationModes.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {REPRESENTATION_MODE_LABELS[mode] ?? mode}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>
      )}

      <Stack spacing={1.5}>
        <Typography variant="subtitle2" fontWeight={700}>
          Paramètres du type de seuil
        </Typography>

        <ThresholdParametersField
          schema={controller.parameterSchema}
          uiSchema={controller.parameterUiSchema as Record<string, unknown>}
          formData={formValues.definition.parameters}
          isLoading={controller.isParameterSchemaLoading}
          errorMessage={controller.parameterSchemaError}
          onChange={(nextValue) =>
            controller.setDefinitionField('parameters', nextValue)
          }
        />
      </Stack>

      <ThresholdPeriodRangePicker
        label="Période active"
        value={[
          formValues.definition.active_from,
          formValues.definition.active_to,
        ]}
        onChange={([activeFrom, activeTo]) => {
          controller.setDefinitionField('active_from', activeFrom);
          controller.setDefinitionField('active_to', activeTo);
        }}
        helperText={errors.definition_active_period ?? 'Optionnel'}
        error={Boolean(errors.definition_active_period)}
      />
    </Stack>
  );
}
